<script setup lang="ts">
import { tracking } from '~/services/tracking.client'

import {computed, ref} from "vue";
import type {ConversionsInterface, ProductListInterface} from "~~/server/types/Products";
import {usePrice} from "~/composables/usePrice";
import {useCart} from "~/composables/useCart";
import {useFavorites} from "~/composables/useFavorites";
import type {ProductOptionsInterface} from "~~/server/types/ProductOptions";
import {rememberProductListPosition} from "~/composables/useProductListPosition";

const { format: price, oldPrice } = usePrice();
const { addToCart,adding } = useCart();
const { in_favorites,addToFavorites,removeFromFavorites,adding:favLoading } = useFavorites();
const productsStore=useProductStore();

const { t } = useI18n();
const localePath = useLocalePath();


const props = withDefaults(defineProps<{
    list: ProductListInterface[];
    allOptions?: ProductOptionsInterface[];
}>(), {
  allOptions: () => [],
});

type ProductState = {
    conversion: ConversionsInterface;
    count: number;
};

const productState = ref<Record<number, ProductState>>({});

const currentProductState = (item: ProductListInterface): ProductState => {
    if (!productState.value[item.id]) {
        const defaultConversion: ConversionsInterface =
            item.conversions.find((conv) => conv.code === "PCS") ||
            item.conversions[0] ||
            ({} as ConversionsInterface);

        productState.value[item.id] = {
            conversion: defaultConversion,
            count: 1,
        };
    }
    return productState.value[item.id]!;
};

const setConversion = (item: ProductListInterface, conv: ConversionsInterface) => {
    currentProductState(item).conversion = conv;
};

const decrement = (item: ProductListInterface) => {
    const state = currentProductState(item);
    if (state.count > 1) state.count--;
};

const increment = (item: ProductListInterface) => {
    const state = currentProductState(item);
    state.count++;
};

const addItem = async (item: ProductListInterface) => {
    const state = currentProductState(item);
    if (!state.conversion || state.count <= 0) return;
    await addToCart(item.id, state.conversion.code, state.conversion.conversion, state.count);
};


const goToProduct = (item: ProductListInterface) => {
    tracking.selectItem(item, trackingListId(), props.list.findIndex(p => p.id === item.id));
    rememberProductListPosition(item.id);
    navigateTo(localePath(item.url));
}

const rememberProductFromClick = (event: MouseEvent, item: ProductListInterface) => {
    const target = event.target;

    if (target instanceof Element && target.closest('[data-product-link]')) {
        tracking.selectItem(item, trackingListId(), props.list.findIndex(p => p.id === item.id));
        rememberProductListPosition(item.id);
    }
}

type ProductGridOption = {
  id: number;
  name: string;
  order: number;
};

const visibleOptionValues = computed<Map<number, ProductGridOption>>(() => {
  const map = new Map<number, ProductGridOption>();
  let order = 0;

  for (const option of productsStore.options) {
    if (!option.show_in_grid) continue;

    for (const value of option.values) {
      map.set(value.id, {
        id: value.id,
        name: value.name,
        order: order++,
      });
    }
  }

  return map;
});

const productOptionsIndex = computed<Record<number, ProductGridOption[]>>(() => {
  const rows = props.allOptions;
  const visibleValues = visibleOptionValues.value;

  if (!rows.length || !visibleValues.size) return {};

  const out: Record<number, ProductGridOption[]> = {};

  for (const row of rows) {
    const value = visibleValues.get(row.options_value_id);
    if (!value) continue;

    (out[row.product_id] ??= []).push(value);
  }

  for (const productId in out) {
    out[productId]?.sort((a, b) => a.order - b.order);
  }

  return out;
});

const getProductOptions = (id: number): ProductGridOption[] => {
  return productOptionsIndex.value[id] ?? [];
};

const trackingRoute = useRoute()
const trackingListId = () => `products:${trackingRoute.path}`
onMounted(() => {
    let previousList = ''
    watch(() => props.list, list => {
        const key = list.map(item => item.id).join(',')
        if (key !== previousList) {
            previousList = key
            tracking.viewItemList(list, trackingListId())
        }
    }, { immediate: true })
})
</script>


<template>
    <div class="row">
        <div
                v-for="(item, index) in props.list?.filter((i) => currentProductState(i)?.conversion)"
                :key="item.id"
                :data-product-id="item.id"
                class="col-md-3 col-6 mb-5"
        >

            <article class="product-card" @click.capture="rememberProductFromClick($event, item)">
                <div class="fav-btn">
                    <el-button aria-label="Add to favorites" circle class="btn" :loading="favLoading?.[item.id]" @click="addToFavorites(item)" v-if="!in_favorites(item.id)">
                        <template #icon>
                            <Icon name="lets-icons:favorite-light" />
                        </template>
                    </el-button>
                    <el-button aria-label="Remove from favorites" circle class="btn"  type="primary" :loading="favLoading?.[item.id]" @click="removeFromFavorites(item)" v-else>
                        <template #icon>
                            <Icon name="lets-icons:favorite-fill" />
                        </template>
                    </el-button>
                </div>
              <div v-if="currentProductState(item).conversion.discount?.[0]"  type="primary" class="discount-badge fw-bold" >
                 {{-1 * currentProductState(item).conversion.discount![0]!}} %
              </div>
                <NuxtLinkLocale
                    :to="`${item.url}`"
                    :aria-label="item.name"
                    class="product-media"
                    data-product-link
                >
                    <img
                        :src="item.grid"
                        :alt="item.name"
                        class="product-image"
                        :loading="index === 0 ? 'eager' : 'lazy'"
                        :fetchpriority="index === 0 ? 'high' : 'auto'"
                        decoding="async"
                        width="250"
                        height="200"
                        :style="{ aspectRatio: 'var(--img-ratio)' }"
                >
                </NuxtLinkLocale>
                <div class="title-body">
                <NuxtLinkLocale :to="`${item.url}`" :aria-label="item.name" data-product-link>
                    <div class="product-grid-name my-2">{{ item.name }}</div>
                </NuxtLinkLocale>
                </div>
                <el-rate
                        :model-value="item?.rating"
                        :max="5"
                        disabled
                        class="mb-2"
                />
              <div class="product-options-grid pb-3" v-if="getProductOptions(item.id).length">
                <el-tag
                    v-for="option in getProductOptions(item.id)"
                    :key="option.id"
                    type="primary"
                    round
                    effect="light"
                    size="small"
                >
                  {{ option.name }}
                </el-tag>
              </div>

                <div class="price-row mb-1">
                      <div class="price-main d-flex align-items-center justify-content-center">
                          <div class="fw-semibold ">{{price(currentProductState(item).conversion.price)}}</div>
                          <div class="price-secondary ">
                            <div class="fw-bold ms-2 " v-if="currentProductState(item).conversion.discount?.[0]">
                              {{ price(oldPrice(currentProductState(item).conversion.price, currentProductState(item).conversion.discount?.[0] || 0)) }}
                            </div>
                          </div>
                      </div>

                </div>

                <div class="pack-row mb-3">

                  <span v-if="item.conversions.length > 1">
                     {{$t('price_from_pcs',{price:price(currentProductState(item).conversion.price/currentProductState(item).conversion.conversion)})}}
                  </span>
                </div>


                <div v-if="item.can_sell"  class="qty-row mb-3">
                  <el-button  :disabled="!item.can_sell" circle type="primary" text class="qty-button" aria-label="Decrease count"  @click="decrement(item)" >
                    <Icon name="lets-icons:remove-fill"  />
                  </el-button>


                    <input  :disabled="!item.can_sell" v-model="currentProductState(item).count" class="qty-value" aria-label="Quantity" />
                    <el-button  :disabled="!item.can_sell" circle type="primary" text class="qty-button"  @click="increment(item)" aria-label="Increase count" >
                      <Icon name="lets-icons:add-ring-fill" />
                    </el-button>
                </div>
              <div v-show="item.conversions.length > 1"  class="conversion_buttons mb-3" >
                           <el-button
                             v-for="conv in item.conversions.sort((a, b) => a.conversion - b.conversion).slice(0, 2)"
                             :key="conv.code"
                             :disabled="conv.conversion > item.count"
                             size="small"
                             round
                             plain
                             class="conversion_button"
                             :class="{ 'active': currentProductState(item).conversion?.code === conv.code }"
                             aria-label="Change conversion"
                             @click="setConversion(item, conv)"
                        >
                            {{ t(conv.code) }} - {{ conv.conversion }} {{ t("short_"+item.unit) }}
                        </el-button>
                        </div>
                <el-button
                        v-if="item.can_sell"
                        type="primary"
                        round
                        :disabled="adding?.[item.id]"
                        :loading="adding?.[item.id]"
                        class="add-btn"
                        aria-label="Add to cart"
                        @click="addItem(item)"
                >

                    {{ t("add") }}
                </el-button>
                <NuxtLinkLocale
                    v-else
                    data-product-link
                    class="add-btn el-button el-button--primary is-plain is-round"
                    aria-label="Add to cart"
                    @click="goToProduct(item)"
                >

                    {{ t("view_product") }}
                </NuxtLinkLocale>
            </article>

        </div>
    </div>
</template>

<style scoped lang="scss">
.product-options-grid {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 100%;
  min-height: calc(22px * 2 + 8px);
}

.product-options-grid .el-tag {
  flex-shrink: 0;
}
.product-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;

  .product-media {
    display: block;
    width: 100%;
    line-height: 0;
  }

  .title-body{
    flex: 1 1 auto;
    min-height: 1px;

    .product-grid-name{
      text-align: center;
      font-weight: var(--fw-semibold);
      line-height: 1.3;
    }
  }
  .fav-btn {
    position: absolute;
    top: 10px;
    right: 14px;
    z-index: 10;
    .btn {
      width: 38px !important;
      height: 38px !important;
    }
  }
.discount-badge {
  position: absolute;
  top: 10px;
  left: 14px;
  z-index: 10;
  font-size: var(--fs--11);
  background: var(--el-color-primary);
  color: white;
  padding: 2px 7px 1px 7px;
  border-radius: 20px;

}

  .product-image {
    height: auto;
    -o-object-fit: contain;
    object-fit: contain;
    width: 100%;
    aspect-ratio: var(--img-ratio);
  }


  .price-row {
    align-items: center;

    .price-main{
      font-size: 1.15rem;
    }
    .price-secondary{
      font-size:  var(--fs--11);
      color: var(--el-color-info-dark-1);
      text-decoration: line-through;
    }
  }

  .pack-row {
    font-size: var(--fs--22);
    color: var(--el-color-info-light-5);
    margin-bottom: 14px;
  }

  .conversion_buttons {
    display: flex;
    align-items: center;
    @media (max-width: 768px) {
      flex-direction: column;
    }
    .conversion_button {
      flex: 1;
      font-size: var(--fs--2);
      --el-button-border-color:transparent;
      margin: 0;
      &.active {
       border-width: 2px;
        --el-button-border-color:var(--el-color-primary);
      }
      @media (max-width: 768px) {
        width: 100%;
        margin-bottom: 10px;
      }
    }
  }

  .add-btn {
    text-transform: uppercase;
    margin-top: auto;
    font-size: var(--fs--22);
    line-height: normal;
    height: 28px;
  }


  }




</style>
